// Saved teams for the game settings: the standard squads plus teams uploaded as team codes, kept in
// this browser (localStorage). Games carry the chosen team's code, so other players don't need the list.

import { TEAMS } from '../../../games/capture-the-flag/capture-the-flag';
import { encodeTeam, decodeTeam } from '../../../games/capture-the-flag/teamcode';

export interface SavedTeam { name: string; code: string; builtIn?: boolean }

// The built-in teams, the default (Three squires) first.
export const BUILT_IN_TEAMS: SavedTeam[] = Object.values(TEAMS).map(t => ({ name: t.name, code: encodeTeam(t), builtIn: true }));

const KEY = 'ctf-saved-teams-v1';

// A saved team can't share a built-in team's name: such entries are old copies of a built-in team
// (built-in teams change between releases), so they are dropped, and new saves get " (custom)".
const BUILT_IN_NAMES = new Set(BUILT_IN_TEAMS.map(t => t.name));

export function loadSavedTeams(): SavedTeam[] {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '[]') as SavedTeam[];
    if (!Array.isArray(raw)) return [];
    const ok = raw.filter(t => typeof t?.name === 'string' && typeof t?.code === 'string');
    const kept = ok.filter(t => !BUILT_IN_NAMES.has(t.name));
    if (kept.length !== ok.length) store(kept);
    return kept;
  } catch {
    return [];
  }
}

function store(teams: SavedTeam[]) {
  try { localStorage.setItem(KEY, JSON.stringify(teams)); } catch { /* not kept: storage blocked */ }
}

// Saves a team code under a name (replacing a saved team of the same name); throws when the code is bad.
export function saveTeam(name: string, code: string): SavedTeam {
  const team = decodeTeam(code);
  let entryName = name.trim() || team.name;
  if (BUILT_IN_NAMES.has(entryName)) entryName += ' (custom)';
  const entry: SavedTeam = { name: entryName, code: code.trim() };
  store([...loadSavedTeams().filter(t => t.name !== entry.name), entry]);
  return entry;
}

export function removeTeam(name: string) {
  store(loadSavedTeams().filter(t => t.name !== name));
}
